/**
 * The 5.0 band vocabulary — calibrated per-method tier boundaries.
 *
 * Confirmed 2026-08-07 (`Band Calibration.dc.html`): the non-default methods
 * take empirical accuracy-optimal cuts against ΔE2000's settled tiers,
 * rounded to display precision and scored on rounded values. The numbers here
 * are generated by `scripts/calibrate-bands.ts` (algorithm in
 * `band-calibration.ts`) and guarded by a parity test — do not hand-edit;
 * re-run the script if `dyes.json` changes.
 *
 * Three standing rules, whatever the method:
 * 1. Print the method wherever a tier or verdict appears.
 * 2. Never compare a tier across methods.
 * 3. Only ΔE2000's bands follow the user's match line — every other method's
 *    bands are fixed, and copy must say so (use
 *    {@link classifyBandTierWithCuts} for the ΔE2000-with-user-slider case).
 *
 * Notes carried from the study:
 * - ΔEOK prints raw (dp 3); a ×100 display unit converts exactly (same cuts).
 * - DISTINGUISH % derives exactly from RGB DIST (identical ranks); its bands
 *   are computed from RGB DIST's cuts, never calibrated separately. Integer
 *   rounding creates ties — orderings need a sort fallback and ties badge TIE.
 * - RATIO is not a distance (lightness only) and never joins the tier
 *   vocabulary; its calibrated cuts exist only for Accessibility + Comparison,
 *   with the Accessibility top band anchored at 3:1 (WCAG 1.4.11). A first
 *   band of 1.00 is literal: that tier is unreachable through lightness alone.
 */
import { COLOR_DISTANCE_MAX } from '../constants/index.js';
/** Display precision per method (identifiers, not quantities — never localise). */
export const BAND_METHOD_DP = {
    ciede2000: 1,
    oklab: 3,
    cie76: 1,
    redmean: 1,
    rgb: 1,
    distinguish: 0,
};
/** Derive DISTINGUISH % cuts from RGB DIST cuts (the one legal derivation). */
export function deriveDistinguishCuts(rgbdistCuts) {
    return rgbdistCuts.map((v) => Math.round((v / COLOR_DISTANCE_MAX) * 100));
}
function bandSet(cuts, method) {
    return { cuts, dp: BAND_METHOD_DP[method] };
}
const RGBDIST_CUTS = {
    match: [8.3, 18.9, 55.5],
    harmony: [11.2, 26.1, 55.5],
    separation: [34.7, 58.9, 129.9],
};
/**
 * The calibrated band vocabulary. ΔE2000 rows are the settled ground truth
 * (MATCH 5/10/20 · HARMONY 6/12/20 · SEPARATION 8/15/30 ascending — the UI
 * reads SEPARATION descending, 30/15/8, high = clear).
 *
 * ⚠️ The **oklab rows were recalibrated in 5.1.0** when `getDeltaE_Oklab`
 * became ΔEOK2 (CSS Color 4 §20.4 — `a` and `b` scaled by 2). That change
 * moves the metric's SCALE, not just its ranking, so the old cuts
 * (match 0.017/0.04/0.107 · harmony 0.02/0.053/0.107 ·
 * separation 0.062/0.103/0.252) would have mis-banded every oklab verdict —
 * a "very close" match reading as merely "close". Regenerate with
 * `pnpm --filter @xivdyetools/core run calibrate:bands`; the parity test
 * recomputes these and fails if they drift.
 */
export const BAND_VOCABULARY = {
    match: {
        ciede2000: bandSet([5, 10, 20], 'ciede2000'),
        oklab: bandSet([0.033, 0.081, 0.174], 'oklab'),
        cie76: bandSet([5.4, 12.6, 26.2], 'cie76'),
        redmean: bandSet([13.9, 31.9, 91.1], 'redmean'),
        rgb: bandSet(RGBDIST_CUTS.match, 'rgb'),
        distinguish: bandSet(deriveDistinguishCuts(RGBDIST_CUTS.match), 'distinguish'),
    },
    harmony: {
        ciede2000: bandSet([6, 12, 20], 'ciede2000'),
        oklab: bandSet([0.041, 0.101, 0.174], 'oklab'),
        cie76: bandSet([6.7, 14.5, 26.2], 'cie76'),
        redmean: bandSet([16.5, 44.4, 91.1], 'redmean'),
        rgb: bandSet(RGBDIST_CUTS.harmony, 'rgb'),
        distinguish: bandSet(deriveDistinguishCuts(RGBDIST_CUTS.harmony), 'distinguish'),
    },
    separation: {
        ciede2000: bandSet([8, 15, 30], 'ciede2000'),
        oklab: bandSet([0.08, 0.144, 0.281], 'oklab'),
        cie76: bandSet([10.4, 19.3, 38.4], 'cie76'),
        redmean: bandSet([54, 102, 222], 'redmean'),
        rgb: bandSet(RGBDIST_CUTS.separation, 'rgb'),
        distinguish: bandSet(deriveDistinguishCuts(RGBDIST_CUTS.separation), 'distinguish'),
    },
};
/**
 * RATIO (WCAG contrast) bands — Accessibility Checker and Dye Comparison
 * only. Not a distance; prints last after a rule; per-tool cuts and polarity;
 * ordering still sorts ascending. dp 2.
 */
export const RATIO_BANDS = {
    /** Calibrated vs MATCH tiers. All 1.00: no lightness cut separates the
     *  lower tiers at all on real pairs — a finding about RATIO, not a bug. */
    comparison: { cuts: [1, 1, 1], dp: 2 },
    /** Calibrated vs SEPARATION tiers on the pooled lens corpus, top band
     *  anchored at 3:1 (the WCAG 1.4.11 figure). */
    accessibility: { cuts: [1, 1.29, 3], dp: 2 },
};
/** Round a value to a method's display precision (tier-on-displayed-value). */
export function roundToBandDisplay(value, method) {
    const f = Math.pow(10, BAND_METHOD_DP[method]);
    return Math.round(value * f) / f;
}
/**
 * Classify a measured value against explicit cuts, scoring on the
 * display-rounded value. Use this directly for ΔE2000 when the user's match
 * line has moved the MATCH cuts; every other method's cuts are fixed.
 */
export function classifyBandTierWithCuts(value, cuts, dp) {
    const f = Math.pow(10, dp);
    const v = Math.round(value * f) / f;
    return v < cuts[0] ? 0 : v < cuts[1] ? 1 : v < cuts[2] ? 2 : 3;
}
/**
 * Classify a measured value into its band tier for a method + context.
 * The value must be in the method's native unit (ΔEOK raw, not ×100).
 */
export function classifyBandTier(value, method, context) {
    const { cuts, dp } = BAND_VOCABULARY[context][method];
    return classifyBandTierWithCuts(value, cuts, dp);
}
//# sourceMappingURL=band-vocabulary.js.map